import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Upload, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { type ApexSubField, isApexList, normaliseValue, parseApexList } from '../api/apexParse';

interface UploadState {
  status: 'idle' | 'uploading' | 'processing' | 'success' | 'error';
  file: File | null;
  data: any;
  error: string | null;
}

const DOCUMENT_TYPES = [
  { value: 'certificate_of_incorporation', label: 'Certificate of Incorporation' },
  { value: 'certificate_of_incumbency', label: 'Certificate of Incumbency' },
  { value: 'license', label: 'License' },
  { value: 'passport', label: 'Passport' },
  { value: 'proof_of_address', label: 'Proof of Address' },
];

function ConfidenceBadge({ score }: { score?: number }) {
  if (score == null) return <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-label-sm)' }}>—</span>;
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: 'var(--radius-sm)',
      fontSize: 'var(--text-label-sm)',
      fontWeight: 'var(--weight-semibold)',
      background: score >= 0.95 ? 'var(--color-success-surface)' : score >= 0.8 ? 'var(--color-warning-surface)' : 'var(--color-error-surface)',
      color: score >= 0.95 ? 'var(--color-success-text)' : score >= 0.8 ? 'var(--color-warning-text)' : 'var(--color-error-text)',
    }}>
      {Math.round(score * 100)}%
    </span>
  );
}

// --- Rendering ---
// Apex string parsing lives in api/apexParse.ts (shared with the form mapper).

function EmptyCell() {
  return <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>—</span>;
}

function ArraySubTable({ fieldName, rows, index }: { fieldName: string; rows: ApexSubField[][]; index: number }) {
  const columns = rows[0]?.map(sf => sf.name) ?? [];
  return (
    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: index * 0.05 }}>
      <td style={{ padding: 'var(--spacing-md)', fontSize: 'var(--text-body-sm)', fontWeight: 'var(--weight-medium)', color: 'var(--color-text-primary)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
        {fieldName}
      </td>
      <td colSpan={2} style={{ padding: 'var(--spacing-sm) var(--spacing-md)' }}>
        <div style={{ border: '1px solid var(--color-outline-variant)', borderRadius: 'var(--radius-md)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-container-low)' }}>
                {columns.map(col => (
                  <th key={col} style={{ padding: 'var(--spacing-sm) var(--spacing-md)', textAlign: 'left', fontSize: 'var(--text-label-sm)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', borderBottom: '1px solid var(--color-outline-variant)', whiteSpace: 'nowrap' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((subFields, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: rowIndex < rows.length - 1 ? '1px solid var(--color-outline-subtle)' : 'none' }}>
                  {columns.map(col => {
                    const sf = subFields.find(f => f.name === col);
                    const val = normaliseValue(sf?.value);
                    return (
                      <td key={col} style={{ padding: 'var(--spacing-sm) var(--spacing-md)', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-primary)', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                          {val ? <span>{val}</span> : <EmptyCell />}
                          {sf?.confidence != null && <ConfidenceBadge score={sf.confidence} />}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </motion.tr>
  );
}

function FieldRows({ field, index, total }: { field: any; index: number; total: number }) {
  const isLast = index === total - 1;
  const borderStyle = isLast ? 'none' : '1px solid var(--color-outline-subtle)';

  if (isApexList(field.value)) {
    const rows = parseApexList(field.value);
    if (rows && rows.length > 0) {
      return <ArraySubTable fieldName={field.name} rows={rows} index={index} />;
    }
  }

  const val = normaliseValue(field.value);
  const confidence = field.confidence ?? field.confidence_score;

  return (
    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: index * 0.05 }} style={{ borderBottom: borderStyle }}>
      <td style={{ padding: 'var(--spacing-md)', fontSize: 'var(--text-body-sm)', fontWeight: 'var(--weight-medium)', color: 'var(--color-text-primary)' }}>
        {field.name}
      </td>
      <td style={{ padding: 'var(--spacing-md)', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-primary)' }}>
        {val ?? <EmptyCell />}
      </td>
      <td style={{ padding: 'var(--spacing-md)', textAlign: 'center' }}>
        <ConfidenceBadge score={confidence} />
      </td>
    </motion.tr>
  );
}

export function DocumentUploadPage() {
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    file: null,
    data: null,
    error: null,
  });

  const [dragActive, setDragActive] = useState(false);
  const [documentType, setDocumentType] = useState<string>('certificate_of_incorporation');

  const handleFileChange = (file: File | null) => {
    if (!file) return;

    if (file.type !== 'application/pdf' && file.type !== 'image/png') {
      setUploadState({ status: 'error', file, data: null, error: 'Please upload a PDF or PNG file' });
      return;
    }

    // Salesforce DocAI processing limit (contentDocumentId path).
    if (file.size > 10 * 1024 * 1024) {
      setUploadState({ status: 'error', file, data: null, error: 'File is too large. Please upload a file smaller than 10MB.' });
      return;
    }

    setUploadState({ status: 'uploading', file, data: null, error: null });
    uploadFile(file);
  };

  const getReadableError = (errorMessage: string): string => {
    // The endpoint sits behind session auth now that extractions persist.
    if (errorMessage.includes('unauthorized') || errorMessage.includes('Authentication required')) {
      return 'Your admin session has expired. Sign in again, then retry.';
    }

    // Try to extract the actual error message from nested errors
    if (errorMessage.includes('TEMPLATE_NOT_FOUND')) {
      const docTypeMatch = errorMessage.match(/documentType:\s*(\w+)/);
      const docType = docTypeMatch ? docTypeMatch[1].replace(/_/g, ' ') : 'selected document type';
      return `Document type "${docType}" is not yet configured in the system. Please select one of the available document types.`;
    }

    if (errorMessage.includes('MISSING_FIELD')) {
      return 'Required field is missing. Please try uploading the document again.';
    }

    if (errorMessage.includes('AUTH_FAILED') || errorMessage.includes('access token')) {
      return 'Authentication with document processing service failed. Please contact support.';
    }

    if (errorMessage.includes('CALLOUT_FAILED') || errorMessage.includes('timeout')) {
      return 'Document processing service is temporarily unavailable. Please try again in a few moments.';
    }

    if (errorMessage.includes('File too large') || errorMessage.includes('file_too_large') || errorMessage.includes('10MB processing limit')) {
      return 'File is too large. Please upload a file smaller than 10MB.';
    }

    // Default: try to extract a clean message
    const messageMatch = errorMessage.match(/"message":"([^"]+)"/);
    if (messageMatch) {
      return messageMatch[1];
    }

    // If all else fails, show a generic message
    return 'Failed to process document. Please try again or contact support if the problem persists.';
  };

  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    try {
      setUploadState(prev => ({ ...prev, status: 'processing' }));
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/v1/admin/documents/process`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        // The API error envelope is {error: {code, message}}; older error
        // shapes were plain strings. Normalize before pattern-matching.
        const errorMessage = typeof data.error === 'string'
          ? data.error
          : data.error
            ? `${data.error.code}: ${data.error.message}`
            : 'Failed to process document';
        throw new Error(getReadableError(errorMessage));
      }

      setUploadState({ status: 'success', file, data: data.extractedData || data, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
      }));
    }
  };

  const resetUpload = () => {
    setUploadState({ status: 'idle', file: null, data: null, error: null });
  };

  return (
    <div style={{ padding: 'var(--spacing-lg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%', maxWidth: '800px' }}>

        <div style={{ marginBottom: 'var(--spacing-xl)', textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-display-sm)', fontWeight: 'var(--weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            DocAI testing tool
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-md)' }}>
            Upload a document to inspect the raw Salesforce DocAI extraction
          </p>
        </div>

        <motion.div layout style={{ background: 'var(--color-surface-container-lowest)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-ambient)', padding: 'var(--spacing-xl)' }}>
          <AnimatePresence mode="wait">

            {uploadState.status === 'idle' && (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* Document Type Selector */}
                <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                  <label htmlFor="document-type" style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 'var(--text-label-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-sm)' }}>
                    Document Type
                  </label>
                  <select
                    id="document-type"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    style={{
                      width: '100%',
                      height: '56px',
                      padding: '0 var(--spacing-md)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-body-md)',
                      color: 'var(--color-text-primary)',
                      background: 'var(--color-surface-container-low)',
                      border: '1px solid var(--color-outline-variant)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Upload Zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFileChange(e.dataTransfer.files[0]); }}
                  onClick={() => document.getElementById('file-input')?.click()}
                  style={{
                    minHeight: '200px',
                    border: `2px dashed ${dragActive ? 'var(--color-action-primary)' : 'var(--color-outline-variant)'}`,
                    borderRadius: 'var(--radius-lg)',
                    background: dragActive ? 'var(--upload-zone-hover-bg)' : 'var(--upload-zone-bg)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--spacing-xl)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  <Upload size={48} style={{ color: 'var(--color-action-primary)', marginBottom: 'var(--spacing-md)' }} />
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-lg)', fontWeight: 'var(--weight-medium)', color: 'var(--color-text-primary)', margin: 0 }}>
                    Drop your PDF or PNG here or click to browse
                  </p>
                  <input id="file-input" type="file" accept=".pdf,application/pdf,.png,image/png" onChange={(e) => handleFileChange(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                </div>
              </motion.div>
            )}

            {(uploadState.status === 'uploading' || uploadState.status === 'processing') && (
              <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                  <Loader2 size={64} style={{ color: 'var(--color-action-primary)', marginBottom: 'var(--spacing-lg)' }} />
                </motion.div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                  <FileText size={20} style={{ color: 'var(--color-text-secondary)' }} />
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-primary)', margin: 0 }}>
                    {uploadState.file?.name}
                  </p>
                </div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-lg)', fontWeight: 'var(--weight-medium)', color: 'var(--color-text-primary)', margin: 0 }}>
                  {uploadState.status === 'uploading' ? 'Uploading...' : 'Processing your document...'}
                </p>
              </motion.div>
            )}

            {uploadState.status === 'success' && uploadState.data && (
              <motion.div key="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {/* Document Info */}
                {(uploadState.data.documentType || uploadState.data.status) && (
                  <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
                    {uploadState.data.documentType && (
                      <div style={{ flex: 1, padding: 'var(--spacing-md)', background: 'var(--color-info-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-info-border)' }}>
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-label-sm)', color: 'var(--color-info-text)', margin: 0, marginBottom: 'var(--spacing-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Document Type
                        </p>
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-info-text)', margin: 0 }}>
                          {uploadState.data.documentType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </p>
                      </div>
                    )}
                    {uploadState.data.status && (
                      <div style={{ flex: 1, padding: 'var(--spacing-md)', background: 'var(--color-success-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-success-border)' }}>
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-label-sm)', color: 'var(--color-success-text)', margin: 0, marginBottom: 'var(--spacing-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Status
                        </p>
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-success-text)', margin: 0, textTransform: 'capitalize' }}>
                          {uploadState.data.status}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Fields Table */}
                {uploadState.data.fields && Array.isArray(uploadState.data.fields) && uploadState.data.fields.length > 0 && (
                  <div style={{ marginBottom: 'var(--spacing-lg)', overflow: 'auto', border: '1px solid var(--color-outline-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
                      <thead>
                        <tr style={{ background: 'var(--color-surface-container-low)' }}>
                          <th style={{ padding: 'var(--spacing-md)', textAlign: 'left', fontSize: 'var(--text-label-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', borderBottom: '2px solid var(--color-outline-variant)' }}>Field</th>
                          <th style={{ padding: 'var(--spacing-md)', textAlign: 'left', fontSize: 'var(--text-label-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', borderBottom: '2px solid var(--color-outline-variant)' }}>Value</th>
                          <th style={{ padding: 'var(--spacing-md)', textAlign: 'center', fontSize: 'var(--text-label-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', borderBottom: '2px solid var(--color-outline-variant)', width: '100px' }}>Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadState.data.fields.map((field: any, index: number) => (
                          <FieldRows
                            key={index}
                            field={field}
                            index={index}
                            total={uploadState.data.fields.length}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <button onClick={resetUpload} style={{
                  width: '100%',
                  height: '56px',
                  background: 'var(--color-action-primary)',
                  color: 'var(--color-text-on-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-semibold)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}>
                  Upload Another Document
                </button>
              </motion.div>
            )}

            {uploadState.status === 'error' && (
              <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <AlertCircle size={64} style={{ color: 'var(--color-error-text)', marginBottom: 'var(--spacing-lg)' }} />
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-title-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', margin: 0, marginBottom: 'var(--spacing-md)' }}>
                  Something went wrong
                </p>
                <div style={{ marginBottom: 'var(--spacing-lg)', padding: 'var(--spacing-md)', background: 'var(--color-error-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-error-border)' }}>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', color: 'var(--color-error-text)', margin: 0 }}>
                    {uploadState.error}
                  </p>
                </div>
                <button onClick={resetUpload} style={{
                  width: '100%',
                  height: '56px',
                  background: 'var(--color-action-primary)',
                  color: 'var(--color-text-on-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-semibold)',
                  cursor: 'pointer',
                }}>
                  Try Again
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </motion.div>
    </div>
  );
}
